import { ProyectoMock } from '../../../../mocks/proyectos';
import { RespuestaAutor, PlazoStatus } from './types';

export function calcPlazo(fechaBase: string): string {
  if (!fechaBase) return '';
  const d = new Date(fechaBase);
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

export function calcDiasRestantes(fechaBase: string): number {
  if (!fechaBase) return 0;
  const plazo = new Date(fechaBase);
  plazo.setDate(plazo.getDate() + 14);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.ceil((plazo.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

export function getPlazoStatus(fechaDecision: string, respondido: boolean): PlazoStatus {
  if (respondido) return 'respondido';
  if (!fechaDecision) return 'sin-fecha';
  const dias = calcDiasRestantes(fechaDecision);
  if (dias < 0) return 'vencido';
  if (dias <= 3) return 'urgente';
  return 'en-plazo';
}

export function calcPuntajePromedio(proyecto: ProyectoMock): number | null {
  if (proyecto.evaluaciones && proyecto.evaluaciones.length > 0) {
    let total = 0;
    let count = 0;
    proyecto.evaluaciones.forEach(ev => {
      Object.values(ev.scores).forEach(v => {
        const n = parseFloat(v);
        if (!isNaN(n)) { total += n; count++; }
      });
    });
    return count > 0 ? Math.round((total / count) * 10) / 10 : null;
  }
  if (proyecto.scores_eval) {
    let total = 0;
    let count = 0;
    Object.values(proyecto.scores_eval).forEach(v => {
      const n = parseFloat(v);
      if (!isNaN(n)) { total += n; count++; }
    });
    return count > 0 ? Math.round((total / count) * 10) / 10 : null;
  }
  return null;
}

export interface EtapaAvance {
  nombre: string;
  descripcion: string;
  peso: number;
  completada: boolean;
  icono: string;
  color: string;
}

// ═══════════════════════════════════════════════════════════════
// METODOLOGÍA DE CÁLCULO DE AVANCE — Proceso de revisión por pares
// ═══════════════════════════════════════════════════════════════
//
// El porcentaje de avance refleja el progreso REAL del proyecto
// a través de las etapas del proceso de revisión por pares.
//
// ETAPAS DEL PROCESO (suma 100%):
//
// 1. Recepción y asignación ........... 10%
//    → Proyecto recibido, código asignado, investigador registrado
//
// 2. Evaluación inicial ............... 20%
//    → Evaluadores asignados, evaluación completada, puntaje calculado
//
// 3. Decisión oficial (1ª ronda) ........ 20%
//    → Decisión del comité emitida, carta de comunicación enviada al autor
//
// 4. Corrección del investigador ...... 15%
//    → Documento corregido recibido del investigador (respuesta a 1ª ronda)
//
// 5. 2ª ronda de revisión ............. 15%
//    → Retroalimentación del comité emitida (2ª ronda de evaluación)
//
// 6. Corrección final / 3ª ronda ...... 10%
//    → Documento corregido recibido (respuesta a 2ª ronda)
//
// 7. Aprobación definitiva .............. 10%
//    → Trabajo aprobado sin correcciones, proceso culminado
//
// NOTA: Las etapas 4-6 son iterativas. Si el comité aprueba en la
// 2ª ronda, se salta la etapa 6 y se suma el 10% de aprobación.
// Máximo 3 rondas de revisión permitidas.
// ═══════════════════════════════════════════════════════════════

export function calcEtapasAvance(
  proyecto: ProyectoMock,
  respuestas: RespuestaAutor[],
  informesAvance: { codigo: string }[] = [],
  retroalimentaciones: { codigo: string; ronda: number; requiereNuevaCorreccion: boolean }[] = []
): EtapaAvance[] {
  const etapas: EtapaAvance[] = [];

  // ── Etapa 1: Recepción y asignación (10%) ──
  const tieneCodigo = proyecto.codigo && proyecto.codigo !== '—' && proyecto.codigo !== '';
  const tieneInvestigador = proyecto.investigador && proyecto.investigador !== '—';
  etapas.push({
    nombre: 'Recepción y asignación',
    descripcion: 'Proyecto recibido, código asignado e investigador registrado',
    peso: 10,
    completada: tieneCodigo && tieneInvestigador,
    icono: 'ri-archive-line',
    color: 'slate',
  });

  // ── Etapa 2: Evaluación inicial (20%) ──
  const tieneEvaluacion = proyecto.evaluaciones && proyecto.evaluaciones.length > 0;
  const tienePuntaje = calcPuntajePromedio(proyecto) !== null;
  etapas.push({
    nombre: 'Evaluación inicial',
    descripcion: 'Evaluadores asignados y evaluación completada con puntaje',
    peso: 20,
    completada: tieneEvaluacion || tienePuntaje,
    icono: 'ri-task-line',
    color: 'teal',
  });

  // ── Etapa 3: Decisión oficial 1ª ronda (20%) ──
  const tieneDecision = proyecto.decision && proyecto.decision !== '—';
  const decisionAprobadaSinCorrecciones =
    tieneDecision &&
    (proyecto.decision?.toLowerCase().includes('científicamente') ||
      (proyecto.decision?.toLowerCase().includes('aprobado') && !proyecto.decision?.toLowerCase().includes('correc')));
  etapas.push({
    nombre: 'Decisión oficial — 1ª ronda',
    descripcion: 'Decisión del comité emitida y carta enviada al investigador',
    peso: 20,
    completada: tieneDecision,
    icono: 'ri-award-line',
    color: 'teal',
  });

  // Si ya está aprobado sin correcciones, las etapas 4-6 se marcan como N/A
  if (decisionAprobadaSinCorrecciones) {
    etapas.push({
      nombre: 'Corrección del investigador',
      descripcion: 'No aplica — trabajo aprobado en 1ª ronda',
      peso: 15,
      completada: true,
      icono: 'ri-check-double-line',
      color: 'emerald',
    });
    etapas.push({
      nombre: '2ª ronda de revisión',
      descripcion: 'No aplica — trabajo aprobado en 1ª ronda',
      peso: 15,
      completada: true,
      icono: 'ri-check-double-line',
      color: 'emerald',
    });
    etapas.push({
      nombre: 'Corrección final / 3ª ronda',
      descripcion: 'No aplica — trabajo aprobado en 1ª ronda',
      peso: 10,
      completada: true,
      icono: 'ri-check-double-line',
      color: 'emerald',
    });
    etapas.push({
      nombre: 'Aprobación definitiva',
      descripcion: 'Trabajo aprobado sin correcciones — proceso culminado',
      peso: 10,
      completada: true,
      icono: 'ri-shield-check-line',
      color: 'emerald',
    });
    return etapas;
  }

  // ── Etapa 4: Corrección del investigador (15%) ──
  // Se completa cuando el investigador envía documento corregido (informe de avance o respuesta ronda 1)
  const respuestasProy = respuestas.filter(r => r.codigo === proyecto.codigo);
  const informesProy = informesAvance.filter(i => i.codigo === proyecto.codigo);
  const tieneCorreccion1 = respuestasProy.some(r => r.ronda === 1) || informesProy.length > 0;
  etapas.push({
    nombre: 'Corrección del investigador',
    descripcion: 'Documento corregido recibido del investigador (respuesta a 1ª ronda)',
    peso: 15,
    completada: tieneCorreccion1,
    icono: 'ri-file-edit-line',
    color: 'amber',
  });

  // ── Etapa 5: 2ª ronda de revisión (15%) ──
  const retro2 = retroalimentaciones.filter(r => r.codigo === proyecto.codigo && r.ronda === 2);
  const tieneRetro2 = retro2.length > 0;
  const retro2Aprobo = retro2.some(r => !r.requiereNuevaCorreccion);
  etapas.push({
    nombre: '2ª ronda de revisión',
    descripcion: 'Retroalimentación del comité emitida (2ª evaluación del documento corregido)',
    peso: 15,
    completada: tieneRetro2,
    icono: 'ri-feedback-line',
    color: 'amber',
  });

  // Si la 2ª ronda aprobó, se salta la etapa 6
  if (retro2Aprobo) {
    etapas.push({
      nombre: 'Corrección final / 3ª ronda',
      descripcion: 'No aplica — trabajo aprobado en 2ª ronda',
      peso: 10,
      completada: true,
      icono: 'ri-check-double-line',
      color: 'emerald',
    });
    etapas.push({
      nombre: 'Aprobación definitiva',
      descripcion: 'Trabajo aprobado en 2ª ronda de revisión — proceso culminado',
      peso: 10,
      completada: true,
      icono: 'ri-shield-check-line',
      color: 'emerald',
    });
    return etapas;
  }

  // ── Etapa 6: Corrección final / 3ª ronda (10%) ──
  const tieneCorreccion2 = respuestasProy.some(r => r.ronda === 2);
  const tieneCorreccion3 = respuestasProy.some(r => r.ronda === 3);
  etapas.push({
    nombre: 'Corrección final / 3ª ronda',
    descripcion: 'Documento corregido recibido (respuesta a 2ª ronda de retroalimentación)',
    peso: 10,
    completada: tieneCorreccion2 || tieneCorreccion3,
    icono: 'ri-file-edit-line',
    color: 'amber',
  });

  // ── Etapa 7: Aprobación definitiva (10%) ──
  const retro3 = retroalimentaciones.filter(r => r.codigo === proyecto.codigo && r.ronda === 3);
  const retro3Aprobo = retro3.some(r => !r.requiereNuevaCorreccion);
  const estadoCulminado =
    proyecto.estado?.toLowerCase().includes('culminad') ||
    proyecto.estado?.toLowerCase().includes('finaliz') ||
    proyecto.estado?.toLowerCase().includes('complet');
  etapas.push({
    nombre: 'Aprobación definitiva',
    descripcion: 'Trabajo aprobado sin correcciones — proceso de revisión culminado',
    peso: 10,
    completada: retro3Aprobo || estadoCulminado,
    icono: 'ri-shield-star-line',
    color: 'emerald',
  });

  return etapas;
}

export function calcPorcentajeAvance(
  proyecto: ProyectoMock,
  respuestas: RespuestaAutor[],
  informesAvance?: { codigo: string }[],
  retroalimentaciones?: { codigo: string; ronda: number; requiereNuevaCorreccion: boolean }[]
): { porcentaje: number; detalle: string[]; rondasCompletadas: number; etapas: EtapaAvance[] } {
  const etapas = calcEtapasAvance(proyecto, respuestas, informesAvance || [], retroalimentaciones || []);
  const detalle: string[] = [];
  let pct = 0;
  let rondasCompletadas = 0;

  etapas.forEach(etapa => {
    if (etapa.completada) {
      pct += etapa.peso;
      detalle.push(`${etapa.nombre}: +${etapa.peso}%`);
    } else {
      detalle.push(`${etapa.nombre}: pendiente (${etapa.peso}%)`);
    }
  });

  // Contar rondas completadas
  const respuestasProy = respuestas.filter(r => r.codigo === proyecto.codigo);
  rondasCompletadas = respuestasProy.length;

  return { porcentaje: Math.min(pct, 100), detalle, rondasCompletadas, etapas };
}

export function getDecisionColor(decision: string) {
  if (
    decision.toLowerCase().includes('científicamente') ||
    (decision.toLowerCase().includes('aprobado') && !decision.toLowerCase().includes('correc'))
  ) {
    return { bg: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: 'ri-checkbox-circle-fill', iconColor: 'text-emerald-600' };
  }
  if (decision.toLowerCase().includes('correc')) {
    return { bg: 'bg-amber-50 border-amber-200 text-amber-700', icon: 'ri-edit-circle-fill', iconColor: 'text-amber-500' };
  }
  if (decision.toLowerCase().includes('no pertinente')) {
    return { bg: 'bg-red-50 border-red-200 text-red-600', icon: 'ri-close-circle-fill', iconColor: 'text-red-500' };
  }
  return { bg: 'bg-slate-50 border-slate-200 text-slate-600', icon: 'ri-award-line', iconColor: 'text-slate-400' };
}

export function getPuntajeBadge(puntaje: number | null) {
  if (puntaje === null) return null;
  if (puntaje >= 8) return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: `${puntaje}/10`, icon: 'ri-star-fill' };
  if (puntaje >= 6) return { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: `${puntaje}/10`, icon: 'ri-star-half-fill' };
  return { cls: 'bg-red-50 text-red-600 border-red-200', label: `${puntaje}/10`, icon: 'ri-star-line' };
}

export function getRondaLabel(ronda: number): string {
  const labels: Record<number, string> = { 1: '1ª Ronda', 2: '2ª Ronda', 3: '3ª Ronda' };
  return labels[ronda] || `Ronda ${ronda}`;
}

export function buildResponseEmail(
  formData: Record<string, string>,
  proyectoTitulo: string,
  tipo: 'respuesta' | 'avance',
  ronda?: number,
  porcentajeCalculado?: number,
  porcentajeDetalle?: string[]
): string {
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  const codigo = formData['codigo_proyecto'] || '';
  const investigador = formData['nombre_investigador'] || '';

  const lines: string[] = [today, '', 'Estimado Comité Científico / UGPP — Vicerrectorado de Investigación, innovacion y desarrollo institucional,', ''];

  if (tipo === 'respuesta') {
    lines.push(`Por medio de la presente, el/la investigador/a ${investigador} da respuesta formal (Ronda ${ronda || 1}) a la decisión emitida por el Comité Científico para el proyecto "${proyectoTitulo}" (código: ${codigo}).`);
    lines.push('');
    lines.push(`─── RESPUESTA A LA DECISIÓN DEL COMITÉ — RONDA ${ronda || 1} ───`);
    lines.push('');
    if (formData['actividades_realizadas']) { lines.push('Actividades realizadas:'); lines.push(formData['actividades_realizadas']); lines.push(''); }
    if (formData['resultados_obtenidos']) { lines.push('Resultados obtenidos:'); lines.push(formData['resultados_obtenidos']); lines.push(''); }
    if (formData['dificultades']) { lines.push('Dificultades:'); lines.push(formData['dificultades']); lines.push(''); }
    const etica = formData['requiere_etica'];
    if (etica && etica !== 'No aplica') {
      lines.push('─── COMITÉ DE ÉTICA ───'); lines.push('');
      lines.push(`Estado: ${etica}`);
      if (formData['numero_resolucion_etica']) lines.push(`Resolución: ${formData['numero_resolucion_etica']}`);
      if (formData['resultado_etica']) lines.push(`Resultado: ${formData['resultado_etica']}`);
      lines.push('');
    }
    if (formData['observaciones_adicionales']) { lines.push('Observaciones adicionales:'); lines.push(formData['observaciones_adicionales']); lines.push(''); }
  } else {
    const porcentaje = porcentajeCalculado !== undefined ? `${porcentajeCalculado}%` : '';
    lines.push(`Informe de avance del proyecto "${proyectoTitulo}" (código: ${codigo})${porcentaje ? `, avance: ${porcentaje}` : ''}.`);
    lines.push('');
    lines.push('─── INFORME DE AVANCE ───');
    lines.push('');
    if (porcentaje) {
      lines.push(`Porcentaje de avance: ${porcentaje}`);
      if (porcentajeDetalle && porcentajeDetalle.length > 0) {
        porcentajeDetalle.forEach(d => lines.push(`  · ${d}`));
      }
      lines.push('');
    }
    if (formData['actividades_realizadas']) { lines.push('Actividades observadas:'); lines.push(formData['actividades_realizadas']); lines.push(''); }
    if (formData['proximas_actividades']) { lines.push('Próximas actividades:'); lines.push(formData['proximas_actividades']); lines.push(''); }
    if (formData['observaciones_avance']) { lines.push('Observaciones del comité:'); lines.push(formData['observaciones_avance']); lines.push(''); }
  }

  lines.push('Atentamente,');
  lines.push('');
  lines.push(investigador || 'Comité Científico / UGPP');
  lines.push('Proyecto: ' + (proyectoTitulo || codigo));

  return lines.join('\n');
}
